/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo, useState } from 'react';
import { Form, Alert, Button } from 'antd';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { FiArrowLeft } from 'react-icons/fi';

import { useAppDispatch } from '@/hooks/useRedux';
import {
  postIntroduction,
  submitIdentifications,
  deliverOtp,
  verifyOtp,
  confirmAccount,
} from '@/redux/auth/registration/thunk';

import type {
  IdentificationPayload,
  VerifyOtpPayload,
} from '@/redux/auth/registration/types';

import type { Step1SessionData } from './signupSteps/types';
import { getSignupSteps } from './signupSteps';
import type { StepItem } from '@/components/common/form/MultiStepForm';

import Logo from '@/components/layout/Logo';
import ThemedButton from '@/components/common/buttons/Button';
import AuthFooter from '../AuthFooter';
import { getProfessionalErrorMessage, extractErrorMessage } from '@/utils/errorHandling';

type ContactInfo = { email: string; phone: string };

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

/* ─── Password strength ──────────────────────────────────────────────────
   Score is additive: each rule adds 1 point (length >= 12 adds an extra).
   Score maps to 1-4 filled bars so the UI never shows "0 bars" once the
   user has started typing.
──────────────────────────────────────────────────────────────────────────── */

type StrengthLevel = 'empty' | 'weak' | 'fair' | 'strong' | 'very_strong';

function getPasswordStrength(password: string): {
  level: StrengthLevel;
  score: number;
  label: string;
  color: string;
} {
  if (!password) return { level: 'empty', score: 0, label: '', color: '' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2)
    return { level: 'weak', score, label: 'Weak', color: '#ef4444' };
  if (score === 3)
    return { level: 'fair', score, label: 'Fair', color: '#f97316' };
  if (score === 4)
    return { level: 'strong', score, label: 'Strong', color: '#22c55e' };
  return {
    level: 'very_strong',
    score,
    label: 'Very strong',
    color: '#16a34a',
  };
}

function PasswordStrengthIndicator({ password }: { password: string }) {
  const { level, score, label, color } = getPasswordStrength(password);

  // Nothing to show until the user starts typing
  if (level === 'empty') return null;

  // score 0-6 mapped to 1-4 filled bars
  const filledBars = score <= 2 ? 1 : score === 3 ? 2 : score === 4 ? 3 : 4;

  const requirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'Uppercase letter' },
    { met: /[0-9]/.test(password), text: 'Number' },
    { met: /[^A-Za-z0-9]/.test(password), text: 'Special character (!@#$...)' },
  ];

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors duration-300"
            style={{ backgroundColor: i < filledBars ? color : '#e5e7eb' }}
          />
        ))}
      </div>

      <p className="text-xs font-medium" style={{ color }}>
        {label}
      </p>

      <ul className="space-y-0.5">
        {requirements.map((req) => (
          <li
            key={req.text}
            className={`flex items-center gap-1.5 text-[11px] ${
              req.met ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            <span>{req.met ? '✓' : '○'}</span>
            {req.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

const SignupPage = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const router = useRouter();

  // All registration state lives here — no localStorage, no auto-resume.
  // A page refresh forces the user back to step 0.
  const [activeStep, setActiveStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1SessionData | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Single lock across all steps — prevents double-submit on slow connections.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Drives PasswordStrengthIndicator without extra state; antd handles the subscription.
  const passwordValue = Form.useWatch('password', form) ?? '';

  /* ─── ?next= redirect helpers ────────────────────────────────────────────
     Reads the base path at runtime so this works regardless of deployment
     prefix. isSafeRelative blocks open-redirect attempts.
  ──────────────────────────────────────────────────────────────────────── */

  const getBasePath = (): string => {
    if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_BASE_PATH) {
      return process.env.NEXT_PUBLIC_BASE_PATH.replace(/\/+$/, '');
    }
    try {
      const bp = (window as any)?.__NEXT_DATA__?.basePath;
      return typeof bp === 'string' ? bp.replace(/\/+$/, '') : '';
    } catch {
      return '';
    }
  };

  const stripBasePath = (p: string): string => {
    const bp = getBasePath();
    if (!bp) return p;
    if (p === bp) return '/';
    return p.startsWith(`${bp}/`) ? p.slice(bp.length) || '/' : p;
  };

  // Reject anything that isn't a single-slash-prefixed relative path
  const isSafeRelative = (p?: string | null): boolean =>
    !!p && /^\/(?!\/)/.test(p);

  const pickNextDestination = (): string => {
    if (typeof window === 'undefined') return '/account';
    const raw = new URLSearchParams(window.location.search).get('next');
    if (!isSafeRelative(raw)) return '/account';
    return stripBasePath(raw!);
  };

  /* ─── Steps ───────────────────────────────────────────────────────────────
     getSignupSteps may return undefined slots for conditional steps.
     We filter before indexing so safeActiveStep never lands on a hole.
  ──────────────────────────────────────────────────────────────────────── */

  const rawSteps = useMemo(
    () => getSignupSteps(form, step1Data, inlineError),
    [form, step1Data, inlineError],
  );

  const steps: StepItem[] = rawSteps.filter(
    (s): s is StepItem => s !== undefined,
  );
  const successIdx = steps.findIndex((s) => s.id === 'success');

  const safeActiveStep = useMemo(
    () => clamp(activeStep, 0, Math.max(0, steps.length - 1)),
    [activeStep, steps.length],
  );

  const currentStep: StepItem | undefined = steps[safeActiveStep] ?? steps[0];

  /* ─── Sidebar progress list ───────────────────────────────────────────────
     Fixed ordered list mapped from the steps array by id. Stays stable even
     if getSignupSteps conditionally omits a step mid-flow.
  ──────────────────────────────────────────────────────────────────────── */

  const progressItems = useMemo(() => {
    const order = [
      'account_type',
      'identification',
      'contact_info',
      'verify_otp',
      'set_password',
    ] as const;

    const byId: Record<string, StepItem> = {};
    steps.forEach((s) => {
      if (s?.id) byId[s.id] = s;
    });

    return order
      .map((id) => {
        const s = byId[id];
        if (!s) return null;
        return {
          id,
          title: s.subtitle || s.title || '',
          desc: (s.description as string) || '',
        };
      })
      .filter((x): x is NonNullable<typeof x> => !!x);
  }, [steps]);

  const progressStep = progressItems.length
    ? Math.min(
        progressItems.length - 1,
        successIdx >= 0 && safeActiveStep >= successIdx
          ? progressItems.length - 1
          : safeActiveStep,
      )
    : 0;

  const heroSrc =
    currentStep?.id === 'verify_otp'
      ? '/images/verifyotp.svg'
      : '/images/signup.svg';

  const primaryButtonLabel = useMemo(() => {
    switch (currentStep?.id) {
      case 'account_type':
        return 'Get started';
      case 'identification':
        return 'Continue';
      case 'contact_info':
        return 'Send verification code';
      case 'verify_otp':
        return 'Verify OTP';
      case 'set_password':
        return 'Create account';
      case 'success':
        return 'Go to My Account';
      default:
        return 'Continue';
    }
  }, [currentStep]);

  /* ─── Session guard ────────────────────────────────────────────────────────
     Called at the top of steps 1-4. If session_id is missing (e.g. the user
     refreshed mid-flow) we reset everything rather than letting a broken
     payload reach the API.
  ──────────────────────────────────────────────────────────────────────── */

  const ensureSessionOrReset = (): boolean => {
    if (!step1Data?.session_id) {
      setInlineError('Your session has expired. Please start again.');
      setStep1Data(null);
      setContactInfo(null);
      setActiveStep(0);
      form.resetFields();
      return false;
    }
    return true;
  };

  const handleStep0Next = async () => {
    setInlineError(null);

    const values = await form.validateFields([
      'account_type',
      'individual_subtype',
      'agree',
    ]);

    const res = await dispatch(
      postIntroduction({
        nationality: 'KE',
        is_alien: false,
        is_refugee: false,
        agreed_terms: values.agree,
        // account_type: values.account_type,
        // individual_subtype: values.individual_subtype,
      }),
    ).unwrap();

    setStep1Data({
      session_id: res.session_id,
      account_type: values.account_type,
      individual_subtype: values.individual_subtype,
    });
    setActiveStep(1);
  };

  const handleStep1Next = async () => {
    setInlineError(null);
    if (!ensureSessionOrReset()) return;

    // Validate the named field; UUID-keyed fields are validated by their own Form.Items
    const values = await form.validateFields(['last_name']);
    const allValues = form.getFieldsValue(true);

    // Fields that are never identification documents
    const excluded = new Set([
      'account_type',
      'individual_subtype',
      'agree',
      'email',
      'phone',
      'otp',
      'password',
      'confirm_password',
      'last_name',
    ]);

    // Only pass through fields whose name is a UUID — these come from fetchIdentifications()
    const identifications = Object.entries(allValues)
      .filter(([key, value]) => {
        if (excluded.has(key) || !value || !String(value).trim()) return false;
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          key,
        );
      })
      .map(([id, value]) => ({ id, value: String(value).trim() }));

    const payload: IdentificationPayload = {
      session_id: step1Data!.session_id,
      last_name: String(values.last_name ?? '').trim(),
      identifications,
    };

    try {
      await dispatch(submitIdentifications(payload)).unwrap();
      setActiveStep(2);
    } catch (err: any) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(
            err,
            'Failed to submit identification details. Please verify your last name and ID number.',
          ),
        ),
      );
    }
  };

  const handleStep2Next = async () => {
    setInlineError(null);
    if (!ensureSessionOrReset()) return;

    const { email, phone } = await form.validateFields(['email', 'phone']);

    try {
      await dispatch(
        deliverOtp({
          session_id: step1Data!.session_id,
          email_address: email,
          phone_number: phone,
        }),
      ).unwrap();
      setContactInfo({ email, phone });
      setActiveStep(3);
    } catch (err: any) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'Failed to send verification code. Please try again.'),
        ),
      );
    }
  };

  const handleStep3Next = async () => {
    setInlineError(null);
    if (!ensureSessionOrReset()) return;

    const { otp } = await form.validateFields(['otp']);

    // contactInfo is guaranteed here — step 2 sets it before advancing
    const payload: VerifyOtpPayload = {
      session_id: step1Data!.session_id,
      otp_code: otp,
      email_address: (contactInfo?.email ?? '').trim(),
      phone_number: (contactInfo?.phone ?? '').trim(),
    };

    try {
      await dispatch(verifyOtp(payload)).unwrap();
      setActiveStep(4);
    } catch (err: any) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'Invalid code. Please check and try again.'),
        ),
      );
    }
  };

  const handleStep4Next = async () => {
    setInlineError(null);
    if (!ensureSessionOrReset()) return;

    const values = await form.validateFields(['password', 'confirm_password']);

    try {
      await dispatch(
        confirmAccount({
          session_id: step1Data!.session_id,
          password1: values.password,
          password2: values.confirm_password,
        }),
      ).unwrap();
      setActiveStep(5);
    } catch (err: any) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'Failed to set password. Please try again.'),
        ),
      );
    }
  };

  /* ─── OTP resend ───────────────────────────────────────────────────────────
     No dedicated resend endpoint — we replay deliverOtp with the contact info
     already stored in state. Falls back to sending the user back to step 2
     if that state is gone.
  ──────────────────────────────────────────────────────────────────────── */

  const handleResendOtp = async () => {
    if (!contactInfo || !step1Data?.session_id) {
      setInlineError(null);
      setActiveStep(2);
      return;
    }

    setIsSubmitting(true);
    setInlineError(null);

    try {
      await dispatch(
        deliverOtp({
          session_id: step1Data.session_id,
          email_address: contactInfo.email,
          phone_number: contactInfo.phone,
        }),
      ).unwrap();
      // Clear the stale value so the user types the fresh code
      form.setFieldValue('otp', '');
    } catch (err: unknown) {
      setInlineError(
        getProfessionalErrorMessage(
          extractErrorMessage(err, 'Failed to resend code. Please go back and try again.'),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Primary submit dispatcher ─────────────────────────────────────────
     All step handlers throw on API failure; the outer finally always releases
     the lock. validateFields also throws (ValidateErrorEntity) on invalid
     input — that bubbles up here and is swallowed correctly; the form renders
     its own inline field errors.
  ──────────────────────────────────────────────────────────────────────── */

  const handleNext = async () => {
    if (isSubmitting) return;

    if (currentStep?.id === 'success') {
      router.replace(pickNextDestination());
      return;
    }

    setIsSubmitting(true);
    try {
      switch (currentStep?.id) {
        case 'account_type':
          await handleStep0Next();
          break;
        case 'identification':
          await handleStep1Next();
          break;
        case 'contact_info':
          await handleStep2Next();
          break;
        case 'verify_otp':
          await handleStep3Next();
          break;
        case 'set_password':
          await handleStep4Next();
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (safeActiveStep > 0 && currentStep?.id !== 'success') {
      setInlineError(null);
      setActiveStep((prev) => Math.max(0, prev - 1));
    }
  };

  const handleCancel = () => {
    setInlineError(null);
    setStep1Data(null);
    setContactInfo(null);
    setActiveStep(0);
    form.resetFields();
  };

  if (successIdx >= 0 && safeActiveStep >= successIdx) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <div className="w-full max-w-[560px] rounded-xl bg-white p-8 shadow-md">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <span className="text-2xl text-green-600">✓</span>
          </div>

          <div className="mb-4 text-center text-sm text-gray-700">
            {steps[successIdx]?.content}
          </div>

          {inlineError && (
            <Alert
              type="error"
              message={<div>{inlineError}</div>}
              showIcon
              className="mb-4"
            />
          )}

          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => router.replace('/')} className="px-6">
              Go to Home Page
            </Button>
            <Button
              type="primary"
              className="px-6"
              onClick={() => router.replace('/account/signin')}
            >
              Go to My Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-stretch justify-center bg-white px-4 md:px-8">
      <div className="flex w-full max-w-[1200px] bg-white">
        {/* Left hero image — hidden on mobile */}
        <div className="hidden md:block md:w-[360px] lg:w-[380px] border-r border-gray-100">
          <div className="relative h-full min-h-[520px]">
            <div className="hidden flex-shrink-0 items-stretch justify-center md:flex">
              <div className="relative h-[100vh] w-[410px] max-w-full">
                <Image
                  src={heroSrc}
                  alt="Sign up illustration"
                  fill
                  className="object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="flex flex-1 flex-col px-6 md:px-10 md:py-12 min-h-[100svh]">
          <div className="mb-6 flex w-[280px] items-center justify-start">
            <Logo width={402} height={124} imgClassName="max-h-[88px]" />
          </div>

          <div className="flex flex-1 flex-col justify-center">
            <div className="mb-6 text-left">
              <h1 className="mb-2 text-3xl font-semibold text-gray-900 md:text-4xl">
                Sign up
              </h1>
              <p className="text-sm text-gray-600">
                Sign up to access and manage all your county services.
              </p>
            </div>

            {inlineError && (
              <Alert
                type="error"
                message={
                  <div>
                    <div>{inlineError}</div>
                    {inlineError.includes('unavailable') && (
                      <div className="mt-2 text-xs">
                        Need help? Contact{' '}
                        <a
                          href="mailto:support@nyandaruapay.gov.ke"
                          className="underline text-blue-600 hover:text-blue-800"
                        >
                          support@nyandaruapay.gov.ke
                        </a>
                      </div>
                    )}
                  </div>
                }
                showIcon
                className="mb-4 text-xs"
              />
            )}

            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              onFinish={handleNext}
              className="w-full"
            >
              {currentStep?.content}

              {/* Password strength — mounts only on the set_password step */}
              {currentStep?.id === 'set_password' && (
                <PasswordStrengthIndicator password={passwordValue} />
              )}

              {/* OTP resend — replays deliverOtp with stored contact info */}
              {currentStep?.id === 'verify_otp' && (
                <div className="mt-1 text-xs text-gray-500">
                  Didn&apos;t receive a code?{' '}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleResendOtp}
                    className="font-semibold text-[var(--color-accent)] underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80 transition-opacity"
                  >
                    Resend code
                  </button>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3">
                <ThemedButton
                  htmlType="submit"
                  theme="primary"
                  variant="solid"
                  text={isSubmitting ? 'Please wait...' : primaryButtonLabel}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="!h-11 !w-full !rounded-md !text-[15px] !font-semibold"
                />

                <div className="flex items-center h-full justify-between text-xs text-gray-600">
                  <ThemedButton
                    theme="accent"
                    variant="outline"
                    text={
                      currentStep?.id === 'account_type' ? 'Cancel' : 'Back'
                    }
                    iconPosition="prefix"
                    icon={<FiArrowLeft />}
                    disabled={isSubmitting}
                    onClick={
                      currentStep?.id === 'account_type'
                        ? handleCancel
                        : handleBack
                    }
                    className="!text-xs"
                  />
                </div>

                <span className="mt-6 text-black">
                  Already have an account?
                </span>

                <div className="mt-0 flex flex-row justify-between items-center w-full text-sm">
                  <Link
                    href="/account/signin/"
                    className="group flex items-center gap-1 !text-[var(--color-accent)]"
                  >
                    <span className="font-semibold group-hover:underline transition-all">
                      Log In
                    </span>
                    <span className="transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                      &rarr;
                    </span>
                  </Link>

                  <span className="text-gray-400 m-0">or</span>

                  <Link
                    href="/account/reset-password/"
                    className="group flex items-center gap-1 !text-[var(--color-accent)]"
                  >
                    <span className="font-semibold group-hover:underline transition-all">
                      Forgot Password
                    </span>
                    <span className="transition-transform translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                      &rarr;
                    </span>
                  </Link>
                </div>
              </div>
            </Form>
          </div>

          <div className="mt-8 text-xs text-gray-400">
            <AuthFooter />
          </div>
        </div>

        {/* Right sidebar — registration steps progress, desktop only */}
        <div className="hidden lg:flex w-[260px] border-l border-gray-100">
          <div className="flex h-full w-full flex-col items-start justify-center px-8 py-10">
            <h3 className="mb-6 text-sm font-semibold text-gray-900">
              Registration Steps
            </h3>

            <div className="space-y-6 text-sm">
              {progressItems.map((item, idx) => {
                const isDone =
                  successIdx >= 0 && safeActiveStep >= successIdx
                    ? true
                    : idx < progressStep;
                const isCurrent = idx === progressStep && !isDone;

                return (
                  <div key={item.id} className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 p-3 flex h-6 w-6 items-center justify-center !rounded-full border text-[11px] ${
                        isDone
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                          : isCurrent
                            ? 'border-[var(--color-primary)] text-[var(--color-primary)] bg-white'
                            : 'border-gray-300 text-gray-400 bg-white'
                      }`}
                    >
                      {isDone ? '✓' : idx + 1}
                    </div>

                    <div>
                      <div
                        className={[
                          'text-xs font-semibold capitalize',
                          isDone
                            ? '!text-gray-800'
                            : isCurrent
                              ? '!text-[var(--color-secondary)] !font-bold'
                              : 'text-gray-800',
                        ].join(' ')}
                      >
                        {item.title}
                      </div>

                      <div
                        className={[
                          'text-[11px]',
                          isDone
                            ? '!text-gray-600'
                            : isCurrent
                              ? '!text-[var(--color-secondary)]'
                              : 'text-gray-600',
                        ].join(' ')}
                      >
                        {item.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
